/*
 * HanlobBot Dashboard - App.js
 *
 * Recent Updates:
 * - Added Master Catalog functionality:
 *   - MasterCatalogView component for listing subfamilies with master catalog data
 *   - MasterCatalogModal component for creating/editing master catalog entries
 *   - Full CRUD operations integrated with /master-catalog API endpoints
 *   - Support for: family selection, subfamily names, materials, general uses,
 *     general specifications (JSON), and general appliances
 */

import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import toast, { Toaster } from 'react-hot-toast';
import API from "./api";
import ProductModal from "./components/ProductModal";
import ProductsView from "./components/ProductsView";
import CampaignModal from "./components/CampaignModal";
// import CampaignsView from "./components/CampaignsView"; // Unused - using CampaignTreeView instead
import CampaignTreeView from "./components/CampaignTreeView";
import AdSetModal from "./components/AdSetModal";
import AdModal from "./components/AdModal";
import CampaignItemDetailsModal from "./components/CampaignItemDetailsModal";
import AdSetsView from "./components/AdSetsView";
import AdsView from "./components/AdsView";
import CampaignProductModal from "./components/CampaignProductModal";
// import CampaignProductsView from "./components/CampaignProductsView"; // Unused
import MasterCatalogView from "./components/MasterCatalogView";
import MasterCatalogModal from "./components/MasterCatalogModal";
import UsosYGruposView from "./components/UsosYGruposView";
import UsosModal from "./components/UsosModal";
import GruposModal from "./components/GruposModal";
import ProductFamilyTreeView from "./components/ProductFamilyTreeView";
import CopyProductModal from "./components/CopyProductModal";
import DuplicateAsColorModal from "./components/DuplicateAsColorModal";
import ProductDetailsModal from "./components/ProductDetailsModal";
import ProductFamilyModal from "./components/ProductFamilyModal";
import ImportProductsModal from "./components/ImportProductsModal";
import Home from "./pages/Home";
import Messages from "./pages/Messages";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Settings from "./pages/Settings";
import InventarioView from "./pages/InventarioView";
import ConsumoView from "./pages/ConsumoView";
import PaymentView from "./pages/PaymentView";
import MapaView from "./pages/MapaView";
import PuntosDeVentaView from "./pages/PuntosDeVentaView";
import OrdersView from "./pages/OrdersView";
import PuntoDeVentaModal from "./components/PuntoDeVentaModal";
import { useAuth } from "./contexts/AuthContext";
import { useTranslation } from "./i18n";
// AuthProvider and ProtectedRoute used in index.js
import UsersView from "./components/UsersView";
import RolesView from "./components/RolesView";
import ProfilesView from "./components/ProfilesView";
import ClickLogsView from "./components/ClickLogsView";
import AdPerformanceView from "./pages/AdPerformanceView";
import AdDetailView from "./pages/AdDetailView";
import GeoDetailView from "./pages/GeoDetailView";
import ConversionsView from "./pages/ConversionsView";
import ManualSalesView from "./pages/ManualSalesView";
import CRMView from "./pages/CRMView";
import CustomerDetailView from "./pages/CustomerDetailView";
import CRMSalesView from "./pages/CRMSalesView";
import CampaignHome from "./pages/CampaignHome";
import TrackedLinksView from "./pages/TrackedLinksView";
import MLImporterView from "./pages/MLImporterView";
import MLPlaygroundView from "./pages/MLPlaygroundView";
import SalesForecastView from "./pages/SalesForecastView";
import WorkflowsView from "./pages/WorkflowsView";
import AiUsageView from "./pages/AiUsageView";
import CustomerSegmentationView from "./pages/CustomerSegmentationView";
import ConversionProbabilityView from "./pages/ConversionProbabilityView";
import ConvoSimulatorView from "./pages/ConvoSimulatorView";
import AdWorkflowAssignView from "./pages/AdWorkflowAssignView";
import PromosView from "./pages/PromosView";
import CampaignIntelligenceView from "./pages/CampaignIntelligenceView";
import MLOrderImportView from "./pages/MLOrderImportView";
import HelpView from "./pages/HelpView";
import TicketsView from "./pages/TicketsView";
import NotificationsView from "./pages/NotificationsView";
import CrossSellView from "./pages/CrossSellView";
import CompanyInfoView from "./pages/CompanyInfoView";
import ReportedConversationsView from "./pages/ReportedConversationsView";
import KillswitchView from "./pages/KillswitchView";
import NukeEmView from "./pages/NukeEmView";
import LiberadoView from "./pages/LiberadoView";
import BannerView from "./pages/BannerView";
import FbCommentReplyView from "./pages/FbCommentReplyView";
import MaintenanceGate from "./components/MaintenanceGate";
import SalesOverviewView from "./pages/SalesOverviewView";
import MessagePerformanceView from "./pages/MessagePerformanceView";
import GcFollowUpView from "./pages/GcFollowUpView";
import ApiHealthAlerts from "./components/ApiHealthAlerts";
import SimulationModeSelector from "./components/SimulationModeSelector";
import useNewVersionCheck from "./hooks/useNewVersionCheck";

const API_URL = process.env.REACT_APP_API_URL || "https://hanbot-production.up.railway.app";
const socket = io(API_URL, {
  reconnection: false, // Don't keep trying to reconnect
  autoConnect: false   // Don't connect automatically
});

function playPopSound() {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(880, ctx.currentTime);
  gain.gain.setValueAtTime(0.15, ctx.currentTime);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.08);
}

const menuItems = [
  // 1. Panel General
  {
    id: "overview",
    labelKey: "menu.overview",
    path: "/",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    )
  },
  // 2. Resumen de Ventas
  {
    id: "sales-overview",
    labelKey: "menu.salesOverview",
    path: "/sales-overview",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    )
  },
  // 3. Notificaciones
  {
    id: "notifications",
    labelKey: "menu.notifications",
    path: "/notifications",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    )
  },
  // 3. Tickets
  {
    id: "tickets",
    labelKey: "menu.tickets",
    path: "/tickets",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    )
  },
  // 4. Conversaciones
  {
    id: "conversations",
    labelKey: "menu.conversations",
    path: "/conversations",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    )
  },
  // 4-bis. Seguimiento Ground Cover — super_user y arriba (no es Playground).
  {
    id: "gc-seguimiento",
    labelKey: "menu.gcFollowUp",
    path: "/playground/seguimiento-ground-cover",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    )
  },
  // 5. CRM
  {
    id: "crm",
    labelKey: "menu.crm",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    ),
    isExpandable: true,
    children: [
      {
        id: "crm-customers",
        labelKey: "menu.crmCustomers",
        path: "/crm",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        )
      },
      {
        id: "crm-sales",
        labelKey: "menu.crmSales",
        path: "/crm/sales",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        )
      }
    ]
  },
  {
    id: "campanas",
    labelKey: "menu.campaigns",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    ),
    isExpandable: true,
    children: [
      // ── Administración ──
      { id: "label-admin", labelKey: "menu.campAdmin", isLabel: true },
      {
        id: "campaigns",
        labelKey: "menu.campaignsSub",
        path: "/campaigns",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        )
      },
      {
        id: "adsets",
        labelKey: "menu.adSets",
        path: "/adsets",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        )
      },
      {
        id: "ads",
        labelKey: "menu.ads",
        path: "/ads",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
        )
      },
      // ── Análisis ──
      { id: "label-analysis", labelKey: "menu.campAnalysis", isLabel: true },
      {
        id: "ad-performance",
        labelKey: "menu.desempeno",
        path: "/ad-performance",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        )
      },
      {
        id: "segmentacion",
        labelKey: "menu.segmentacion",
        path: "/segmentacion",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        )
      },
      // ── Estrategia ──
      { id: "label-strategy", labelKey: "menu.campStrategy", isLabel: true },
      {
        id: "forecast",
        labelKey: "menu.forecast",
        path: "/forecast",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        )
      },
      {
        id: "intelligence",
        labelKey: "menu.intelligence",
        path: "/inteligencia-artificial",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        )
      },
      // ── Agente ──
      { id: "label-agent", labelKey: "menu.campAgent", isLabel: true },
      {
        id: "cross-sell",
        labelKey: "menu.crossSell",
        path: "/cross-sell",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        )
      },
      {
        id: "ad-workflow",
        labelKey: "menu.adWorkflow",
        path: "/anuncio-flujo",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        )
      },
      {
        id: "promos",
        labelKey: "menu.promos",
        path: "/promos",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 8V3a2 2 0 012-2z" transform="translate(-0.5 1)" />
        )
      },
      // ── Herramientas ──
      { id: "label-tools", labelKey: "menu.campTools", isLabel: true },
      {
        id: "tracked-links",
        labelKey: "menu.trackedLinks",
        path: "/tracked-links",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        )
      },
    ]
  },
  // 7. Estadísticas
  {
    id: "estadisticas",
    labelKey: "menu.statistics",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    ),
    isExpandable: true,
    children: [
      {
        id: "analytics",
        labelKey: "menu.analytics",
        path: "/analytics",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        )
      },
      {
        id: "click-logs",
        labelKey: "menu.clicks",
        path: "/click-logs",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        )
      },
      {
        id: "conversions",
        labelKey: "menu.conversions",
        path: "/conversions",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        )
      },
      {
        id: "manual-sales",
        labelKey: "menu.manualSales",
        path: "/manual-sales",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        )
      },
      {
        id: "consumo",
        labelKey: "menu.consumo",
        path: "/consumo",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        )
      }
    ]
  },
  {
    id: "catalogo",
    labelKey: "menu.catalog",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    ),
    isExpandable: true,
    children: [
      {
        id: "familias",
        labelKey: "menu.families",
        path: "/familias",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        )
      },
      {
        id: "usos-grupos",
        labelKey: "menu.usosGrupos",
        path: "/usos-grupos",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        )
      },
      {
        id: "inventario",
        labelKey: "menu.inventory",
        path: "/inventario",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        )
      },
      {
        id: "pos",
        labelKey: "menu.pos",
        path: "/pos",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        )
      }
    ]
  },
  {
    id: "mercadolibre",
    labelKey: "menu.mercadoLibre",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    ),
    isExpandable: true,
    children: [
      {
        id: "ml-orders",
        labelKey: "menu.orders",
        path: "/ml-orders",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        )
      },
      {
        id: "ml-importer",
        labelKey: "menu.importer",
        path: "/ml-importer",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        )
      },
      {
        id: "ml-import",
        labelKey: "menu.mlImport",
        path: "/ml-import",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        )
      }
    ]
  },
  {
    id: "bot",
    labelKey: "menu.bot",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    ),
    isExpandable: true,
    children: [
      {
        id: "bot-sandbox",
        labelKey: "menu.sandbox",
        path: "/bot/simulador",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4v-4z" />
        )
      },
      {
        id: "ai-usage",
        labelKey: "menu.aiUsage",
        path: "/bot/costos-ia",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3V9m-3 8v-1m-4 4h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
        )
      }
    ]
  },
  {
    id: "usuarios",
    labelKey: "menu.users",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    ),
    isExpandable: true,
    children: [
      {
        id: "users",
        labelKey: "menu.usersSub",
        path: "/users",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        )
      },
      {
        id: "roles",
        labelKey: "menu.roles",
        path: "/roles",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        )
      },
      {
        id: "profiles",
        labelKey: "menu.profiles",
        path: "/profiles",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        )
      }
    ]
  },
  {
    id: "playground",
    labelKey: "menu.playground",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    ),
    isExpandable: true,
    children: [
      {
        id: "playground-ml",
        labelKey: "menu.playgroundML",
        path: "/playground/mercado-libre",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        )
      },
      {
        id: "playground-conversion",
        labelKey: "menu.playgroundConversion",
        path: "/playground/conversion",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        )
      },
      {
        id: "playground-simulador",
        labelKey: "menu.simulador",
        path: "/playground/simulador",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        )
      },
      {
        id: "playground-rendimiento",
        labelKey: "menu.messagePerformance",
        path: "/playground/rendimiento-mensajes",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        )
      },
      {
        id: "playground-mapa",
        labelKey: "menu.mapa",
        path: "/playground/mapa",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        )
      },
      {
        id: "workflows",
        labelKey: "menu.workflows",
        path: "/workflows",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM15 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2h-2zM9 6h6m-3 5v-1m0 0V9m3 5h.01" />
        )
      },
      {
        id: "reported-convos",
        labelKey: "menu.reportedConvos",
        path: "/reported-convos",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v18h18M7 14l3-3 3 3 5-5M21 9V3h-6" />
        )
      },
    ]
  },
  {
    id: "company-info",
    labelKey: "menu.companyInfo",
    path: "/company-info",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    )
  },
  // Spec Ops — super_admin only emergency controls.
  {
    id: "spec-ops",
    labelKey: "menu.specOps",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
    ),
    isExpandable: true,
    children: [
      {
        id: "killswitch",
        labelKey: "menu.killswitch",
        path: "/spec-ops/killswitch",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 11-12.728 0M12 3v9" />
        )
      },
      {
        id: "nuke",
        labelKey: "menu.nukeEm",
        path: "/spec-ops/nuke",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        )
      },
      {
        id: "liberado",
        labelKey: "menu.liberado",
        path: "/spec-ops/liberado",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        )
      },
      {
        id: "banner",
        labelKey: "menu.banner",
        path: "/spec-ops/banner",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
        )
      },
      {
        id: "fb-comment-reply",
        labelKey: "menu.fbCommentReply",
        path: "/spec-ops/fb-comment-reply",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 20l.8-4A7.94 7.94 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        )
      },
      {
        id: "pago",
        labelKey: "menu.payment",
        path: "/spec-ops/pago",
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        )
      }
    ]
  },
  {
    id: "settings",
    labelKey: "menu.settings",
    path: "/settings",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    )
  },
  {
    id: "help",
    labelKey: "menu.help",
    path: "/help",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    )
  }
];

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, canAccess, canManageUsers, getLandingPage, logout, simulationMode, stopSimulation, banner, payment, openAiCredit } = useAuth();
  const { t, locale, language, changeLanguage } = useTranslation();
  const updateAvailable = useNewVersionCheck();

  // Correlation heartbeat — on opening ANY tab (route change) and every 30 min while
  // the app is in use, kick a correlation run so convo↔sale matches stay fresh without
  // a cronjob. Throttled to once / 5 min across navigations; the backend now sweeps
  // from the last CORRELATED point, so one call catches the whole gap up to now.
  useEffect(() => {
    if (!user) return; // authenticated sessions only
    let cancelled = false;
    const kick = async () => {
      if (cancelled) return;
      const last = Number(sessionStorage.getItem("corrHeartbeatAt") || 0);
      if (Date.now() - last < 5 * 60 * 1000) return; // don't spam on rapid tab switches
      try {
        const { data } = await API.get("/correlation/status");
        if (cancelled || data?.running) return;
        sessionStorage.setItem("corrHeartbeatAt", String(Date.now()));
        await API.post("/correlation/run");
      } catch { /* heartbeat must never disrupt the UI */ }
    };
    kick(); // fires on every route change (any tab)
    const id = setInterval(kick, 30 * 60 * 1000); // and every 30 min while in use
    return () => { cancelled = true; clearInterval(id); };
  }, [location.pathname, user]);

  // All useState hooks must be called BEFORE any early returns
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, _setFilter] = useState("all"); // eslint-disable-line no-unused-vars
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({}); // Track expanded menu sections
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationStatuses, setConversationStatuses] = useState({});
  const [handoverLoading, setHandoverLoading] = useState({});
  const [conversationFilter, _setConversationFilter] = useState(null); // eslint-disable-line no-unused-vars
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);

  // Products state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState(null);

  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [deleteConfirmCampaign, setDeleteConfirmCampaign] = useState(null);

  // AdSets state
  const [adSets, setAdSets] = useState([]);
  const [showAdSetModal, setShowAdSetModal] = useState(false);
  const [editingAdSet, setEditingAdSet] = useState(null);
  const [parentCampaignId, setParentCampaignId] = useState(null);

  // Ads state
  const [showAdModal, setShowAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [parentAdSetId, setParentAdSetId] = useState(null);

  // Details modal state
  const [detailsItem, setDetailsItem] = useState(null);

  // Unified delete confirmation
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);

  // Campaign Products state
  const [campaignProducts, setCampaignProducts] = useState([]);
  const [, setCampaignProductsLoading] = useState(false);
  const [showCampaignProductModal, setShowCampaignProductModal] = useState(false);
  const [editingCampaignProduct, setEditingCampaignProduct] = useState(null);
  const [deleteConfirmCampaignProduct, setDeleteConfirmCampaignProduct] = useState(null);

  // Master Catalog state
  const [subfamilies, setSubfamilies] = useState([]);
  const [subfamiliesLoading, setSubfamiliesLoading] = useState(false);
  const [showMasterCatalogModal, setShowMasterCatalogModal] = useState(false);
  const [editingSubfamily, setEditingSubfamily] = useState(null);
  const [deleteConfirmSubfamily, setDeleteConfirmSubfamily] = useState(null);

  // Usos state
  const [showUsosModal, setShowUsosModal] = useState(false);
  const [selectedUso, setSelectedUso] = useState(null);
  const [usos, setUsos] = useState([]);

  // Grupos state
  const [showGruposModal, setShowGruposModal] = useState(false);
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [grupos, setGrupos] = useState([]);

  // Puntos de Venta state
  const [showPuntoDeVentaModal, setShowPuntoDeVentaModal] = useState(false);
  const [selectedPuntoDeVenta, setSelectedPuntoDeVenta] = useState(null);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [pointsOfSaleLoading, setPointsOfSaleLoading] = useState(false);

  // Product Families state
  const [productFamilyTree, setProductFamilyTree] = useState([]);
  const [productFamiliesLoading, setProductFamiliesLoading] = useState(false);
  const [showProductFamilyModal, setShowProductFamilyModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [productToCopy, setProductToCopy] = useState(null);
  const [productToColorDuplicate, setProductToColorDuplicate] = useState(null);
  const [productToShowDetails, setProductToShowDetails] = useState(null);
  const [productDetailsParentChain, setProductDetailsParentChain] = useState([]);
  const [importTargetFamily, setImportTargetFamily] = useState(null);
  const [selectedProductFamily, setSelectedProductFamily] = useState(null);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [deleteConfirmProductFamily, setDeleteConfirmProductFamily] = useState(null);

  // Click stats from ClickLog (for analytics)
  const [clickStats, setClickStats] = useState({ bestAd: null, allAds: [] });

  // Ad metrics (impressions, clicks, CTR)
  const [adMetrics, setAdMetrics] = useState({ totalImpressions: 0, totalClicks: 0, ctr: 0 });

  // Top selling product through ads
  const [topProduct, setTopProduct] = useState(null);
  const [topRegion, setTopRegion] = useState(null);
  const [linksToday, setLinksToday] = useState({ served: 0, clicked: 0 });
  const [promotedProducts, setPromotedProducts] = useState(0);
  const [handoffsToday, setHandoffsToday] = useState(0);

  // Analytics page loading state
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/conversations`, {
        credentials: 'include',
        headers: { Authorization: "Bearer hanlob_admin_2025" }
      });
      const data = await res.json();
      setMessages(data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      console.log("Fetching products from:", `${API_URL}/products`);
      const res = await fetch(`${API_URL}/products`);
      console.log("Response status:", res.status);
      const data = await res.json();
      console.log("Response data:", data);
      if (data.success) {
        console.log("Setting products:", data.data?.length, "products");
        setProducts(data.data || []);
      } else {
        console.warn("API returned success=false:", data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      const res = await fetch(`${API_URL}/products/${productId}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        setProducts(products.filter(p => p._id !== productId));
        setDeleteConfirmProduct(null);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      alert(t('alert.errorDeleteProduct'));
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      const url = editingProduct
        ? `${API_URL}/products/${editingProduct._id}`
        : `${API_URL}/products`;
      const method = editingProduct ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(productData)
      });

      const data = await res.json();
      if (data.success) {
        if (editingProduct) {
          setProducts(products.map(p => p._id === editingProduct._id ? data.data : p));
        } else {
          setProducts([data.data, ...products]);
        }
        setShowProductModal(false);
        setEditingProduct(null);
      }
    } catch (error) {
      console.error("Error saving product:", error);
      alert(t('alert.errorSaveProduct'));
    }
  };

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const res = await fetch(`${API_URL}/campaigns/tree`);
      const data = await res.json();
      if (data.success) {
        console.log("Campaigns loaded:", data.data);
        console.log("Active campaigns:", data.data.filter(c => c.active === true));
        setCampaigns(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const fetchClickStats = async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/clicks-by-ad`);
      const data = await res.json();
      if (data.success) {
        setClickStats({ bestAd: data.bestAd, allAds: data.allAds || [] });
      }
    } catch (error) {
      console.error("Error fetching click stats:", error);
    }
  };

  const fetchAdMetrics = async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/ad-metrics`);
      const data = await res.json();
      if (data.success) {
        setAdMetrics(data.metrics);
      }
    } catch (error) {
      console.error("Error fetching ad metrics:", error);
    }
  };

  const fetchTopProducts = async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/top-products`);
      const data = await res.json();
      if (data.success) {
        setTopProduct(data.topProduct);
      }
    } catch (error) {
      console.error("Error fetching top products:", error);
    }
  };

  const fetchTopRegion = async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/top-region`);
      const data = await res.json();
      if (data.success && data.topRegion) {
        setTopRegion(data.topRegion);
      }
    } catch (error) {
      console.error("Error fetching top region:", error);
    }
  };

  const fetchLinksToday = async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/links-today`);
      const data = await res.json();
      if (data.success) {
        setLinksToday({ served: data.served || 0, clicked: data.clicked || 0 });
      }
    } catch (error) {
      console.error("Error fetching links today:", error);
    }
  };

  const fetchPromotedProducts = async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/promoted-products`);
      const data = await res.json();
      if (data.success) setPromotedProducts(data.count || 0);
    } catch (error) {
      console.error("Error fetching promoted products:", error);
    }
  };

  const fetchHandoffsToday = async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/handoffs-today`);
      const data = await res.json();
      if (data.success) setHandoffsToday(data.count || 0);
    } catch (error) {
      console.error("Error fetching handoffs today:", error);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      const res = await fetch(`${API_URL}/campaigns/${campaignId}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        setCampaigns(campaigns.filter(c => c._id !== campaignId));
        setDeleteConfirmCampaign(null);
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      alert(t('alert.errorDeleteCampaign'));
    }
  };

  const handleSaveCampaign = async (campaignData) => {
    const fullUrl = `${API_URL}/campaigns/${editingCampaign?._id}`;
    console.log('=== SAVE CAMPAIGN ===');
    console.log('URL:', fullUrl);
    console.log('DATA:', JSON.stringify(campaignData, null, 2));

    try {
      let response;
      if (editingCampaign) {
        const fetchResponse = await fetch(fullUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(campaignData)
        });
        const data = await fetchResponse.json();
        console.log('RESPONSE:', JSON.stringify(data, null, 2));

        if (data.success) {
          alert('¡Guardado exitosamente! ad.summary: ' + data.data?.ad?.summary);
          setShowCampaignModal(false);
          setEditingCampaign(null);
          fetchCampaigns();
        } else {
          alert('Error: ' + data.error);
        }
      } else {
        response = await API.post(`/campaigns`, campaignData);
        if (response.data.success) {
          setShowCampaignModal(false);
          setEditingCampaign(null);
          fetchCampaigns();
        }
      }
    } catch (error) {
      console.error("ERROR:", error);
      alert("Error: " + error.message);
    }
  };

  // AdSet handlers
  const fetchAdSets = async () => {
    try {
      const res = await fetch(`${API_URL}/adsets`);
      const data = await res.json();
      if (data.success) {
        setAdSets(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching adsets:", error);
    }
  };

  const handleSaveAdSet = async (adSetData) => {
    try {
      let response;
      if (editingAdSet) {
        response = await API.put(`/adsets/${editingAdSet._id}`, adSetData);
      } else {
        response = await API.post(`/adsets`, adSetData);
      }

      if (response.data.success) {
        setShowAdSetModal(false);
        setEditingAdSet(null);
        setParentCampaignId(null);
        // Refetch campaigns tree to show new adset
        fetchCampaigns();
        fetchAdSets(); // Also update adsets list for Ad modal dropdown
      }
    } catch (error) {
      console.error("Error saving adset:", error);
      alert(t('alert.errorSaveAdSet') + ": " + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteAdSet = async (adSetId) => {
    try {
      const res = await fetch(`${API_URL}/adsets/${adSetId}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        fetchCampaigns();
        fetchAdSets();
      }
    } catch (error) {
      console.error("Error deleting adset:", error);
      alert(t('alert.errorDeleteAdSet'));
    }
  };

  // Ad handlers
  const handleSaveAd = async (adData) => {
    try {
      let response;
      if (editingAd) {
        response = await API.put(`/ads/${editingAd._id}`, adData);
      } else {
        response = await API.post(`/ads`, adData);
      }

      if (response.data.success) {
        setShowAdModal(false);
        setEditingAd(null);
        setParentAdSetId(null);
        // Refetch campaigns tree to show new ad
        fetchCampaigns();
      }
    } catch (error) {
      console.error("Error saving ad:", error);
      alert(t('alert.errorSaveAd') + ": " + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteAd = async (adId) => {
    try {
      const res = await fetch(`${API_URL}/ads/${adId}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        fetchCampaigns();
      }
    } catch (error) {
      console.error("Error deleting ad:", error);
      alert(t('alert.errorDeleteAd'));
    }
  };

  // Unified delete handler for tree view
  const handleDeleteItem = async (item) => {
    const itemType = item.type || 'campaign';

    if (itemType === 'campaign') {
      await handleDeleteCampaign(item._id);
    } else if (itemType === 'adset') {
      await handleDeleteAdSet(item._id);
    } else if (itemType === 'ad') {
      await handleDeleteAd(item._id);
    }

    setDeleteConfirmItem(null);
  };

  const fetchCampaignProducts = async () => {
    setCampaignProductsLoading(true);
    try {
      const res = await fetch(`${API_URL}/campaign-products`);
      const data = await res.json();
      if (data.success) {
        setCampaignProducts(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching campaign products:", error);
    } finally {
      setCampaignProductsLoading(false);
    }
  };

  const handleDeleteCampaignProduct = async (campaignProductId) => {
    try {
      const res = await fetch(`${API_URL}/campaign-products/${campaignProductId}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        setCampaignProducts(campaignProducts.filter(cp => cp._id !== campaignProductId));
        setDeleteConfirmCampaignProduct(null);
      }
    } catch (error) {
      console.error("Error deleting campaign product:", error);
      alert(t('alert.errorDeleteCampaignProduct'));
    }
  };

  const handleSaveCampaignProduct = async (campaignProductData) => {
    try {
      const url = editingCampaignProduct
        ? `${API_URL}/campaign-products/${editingCampaignProduct._id}`
        : `${API_URL}/campaign-products`;
      const method = editingCampaignProduct ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(campaignProductData)
      });

      const data = await res.json();
      if (data.success) {
        if (editingCampaignProduct) {
          setCampaignProducts(campaignProducts.map(cp => cp._id === editingCampaignProduct._id ? data.data : cp));
        } else {
          setCampaignProducts([data.data, ...campaignProducts]);
        }
        setShowCampaignProductModal(false);
        setEditingCampaignProduct(null);
      }
    } catch (error) {
      console.error("Error saving campaign product:", error);
      alert(t('alert.errorSaveCampaignProduct'));
    }
  };

  const fetchSubfamilies = async () => {
    setSubfamiliesLoading(true);
    try {
      const res = await fetch(`${API_URL}/master-catalog`);
      const data = await res.json();
      if (data.success) {
        setSubfamilies(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching subfamilies:", error);
    } finally {
      setSubfamiliesLoading(false);
    }
  };

  const handleDeleteSubfamily = async (subfamilyId) => {
    try {
      const res = await fetch(`${API_URL}/master-catalog/${subfamilyId}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        setSubfamilies(subfamilies.filter(s => s._id !== subfamilyId));
        setDeleteConfirmSubfamily(null);
      }
    } catch (error) {
      console.error("Error deleting subfamily:", error);
      alert(t('alert.errorDeleteCatalog'));
    }
  };

  const handleSaveSubfamily = async (subfamilyData) => {
    try {
      const url = editingSubfamily
        ? `${API_URL}/master-catalog/${editingSubfamily._id}`
        : `${API_URL}/master-catalog`;
      const method = editingSubfamily ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(subfamilyData)
      });

      const data = await res.json();
      if (data.success) {
        if (editingSubfamily) {
          setSubfamilies(subfamilies.map(s => s._id === editingSubfamily._id ? data.data : s));
        } else {
          setSubfamilies([data.data, ...subfamilies]);
        }
        setShowMasterCatalogModal(false);
        setEditingSubfamily(null);
      }
    } catch (error) {
      console.error("Error saving subfamily:", error);
      alert(t('alert.errorSaveCatalog'));
    }
  };

  const fetchUsos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/usos`);
      const data = await res.json();
      if (data.success) {
        setUsos(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching usos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUso = async (uso) => {
    const confirmed = window.confirm(
      t('alert.confirmDeleteUso', { name: uso.name })
    );

    if (!confirmed) return;

    try {
      const res = await fetch(`${API_URL}/usos/${uso._id}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        setUsos(usos.filter(u => u._id !== uso._id));
      }
    } catch (error) {
      console.error("Error deleting uso:", error);
      alert(t('alert.errorDeleteUso'));
    }
  };

  const handleSaveUso = async (usoData) => {
    try {
      const url = selectedUso
        ? `${API_URL}/usos/${selectedUso._id}`
        : `${API_URL}/usos`;
      const method = selectedUso ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(usoData)
      });

      const data = await res.json();
      if (data.success) {
        if (selectedUso) {
          setUsos(usos.map(u => u._id === selectedUso._id ? data.data : u));
        } else {
          setUsos([data.data, ...usos]);
        }
        setShowUsosModal(false);
        setSelectedUso(null);
      }
    } catch (error) {
      console.error("Error saving uso:", error);
      alert(t('alert.errorSaveUso'));
    }
  };

  const fetchGrupos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/grupos`);
      const data = await res.json();
      if (data.success) {
        setGrupos(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching grupos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGrupo = async (grupo) => {
    const confirmed = window.confirm(
      t('alert.confirmDeleteGrupo', { name: grupo.name })
    );

    if (!confirmed) return;

    try {
      const res = await fetch(`${API_URL}/grupos/${grupo._id}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        setGrupos(grupos.filter(g => g._id !== grupo._id));
      }
    } catch (error) {
      console.error("Error deleting grupo:", error);
      alert(t('alert.errorDeleteGrupo'));
    }
  };

  const handleSaveGrupo = async (grupoData) => {
    try {
      const url = selectedGrupo
        ? `${API_URL}/grupos/${selectedGrupo._id}`
        : `${API_URL}/grupos`;
      const method = selectedGrupo ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(grupoData)
      });

      const data = await res.json();
      if (data.success) {
        if (selectedGrupo) {
          setGrupos(grupos.map(g => g._id === selectedGrupo._id ? data.data : g));
        } else {
          setGrupos([data.data, ...grupos]);
        }
        setShowGruposModal(false);
        setSelectedGrupo(null);
      }
    } catch (error) {
      console.error("Error saving grupo:", error);
      alert(t('alert.errorSaveGrupo'));
    }
  };

  // Points of Sale CRUD functions
  const fetchPointsOfSale = async () => {
    setPointsOfSaleLoading(true);
    try {
      const res = await fetch(`${API_URL}/points-of-sale`);
      const data = await res.json();
      if (data.success) {
        setPointsOfSale(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching points of sale:", error);
    } finally {
      setPointsOfSaleLoading(false);
    }
  };

  const handleDeletePuntoDeVenta = async (pos) => {
    const confirmed = window.confirm(
      t('alert.confirmDeletePOS', { name: pos.name })
    );

    if (!confirmed) return;

    try {
      const res = await fetch(`${API_URL}/points-of-sale/${pos._id}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        setPointsOfSale(pointsOfSale.filter(p => p._id !== pos._id));
      }
    } catch (error) {
      console.error("Error deleting point of sale:", error);
      alert(t('alert.errorDeletePOS'));
    }
  };

  const handleSavePuntoDeVenta = async (posData) => {
    try {
      const url = selectedPuntoDeVenta
        ? `${API_URL}/points-of-sale/${selectedPuntoDeVenta._id}`
        : `${API_URL}/points-of-sale`;
      const method = selectedPuntoDeVenta ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(posData)
      });

      const data = await res.json();
      if (data.success) {
        if (selectedPuntoDeVenta) {
          setPointsOfSale(pointsOfSale.map(p => p._id === selectedPuntoDeVenta._id ? data.data : p));
        } else {
          setPointsOfSale([data.data, ...pointsOfSale]);
        }
        setShowPuntoDeVentaModal(false);
        setSelectedPuntoDeVenta(null);
      }
    } catch (error) {
      console.error("Error saving point of sale:", error);
      alert(t('alert.errorSavePOS'));
    }
  };

  const fetchProductFamilies = async () => {
    setProductFamiliesLoading(true);
    try {
      const response = await API.get('/product-families/tree');
      if (response.data.success) {
        console.log('Fetched product families:', response.data.data.length, 'roots');
        setProductFamilyTree(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching product families:", error);
    } finally {
      setProductFamiliesLoading(false);
    }
  };

  const handleSaveProductFamily = async (productFamilyData) => {
    try {
      console.log('📥 App.js received data from modal:');
      console.log('   Name:', productFamilyData.name);
      console.log('   Sellable:', productFamilyData.sellable);
      console.log('   onlineStoreLinks:', productFamilyData.onlineStoreLinks);
      console.log('   Full data:', productFamilyData);

      // Use the presence of _id to determine if this is an update (PUT) or create (POST)
      const isUpdate = selectedProductFamily && selectedProductFamily._id;
      console.log('   Is update?', isUpdate, 'ID:', selectedProductFamily?._id);

      const response = isUpdate
        ? await API.put(`/product-families/${selectedProductFamily._id}`, productFamilyData)
        : await API.post('/product-families', productFamilyData);

      if (response.data.success) {
        console.log('✅ Product family saved successfully');
        console.log('   Response data:', response.data.data);
        console.log('   onlineStoreLinks in response:', response.data.data.onlineStoreLinks);
        // Refresh the tree view
        await fetchProductFamilies();
        setShowProductFamilyModal(false);
        setSelectedProductFamily(null);
        setSelectedParentId(null);
      }
    } catch (error) {
      console.error("❌ Error saving product family:", error);
      alert("Error saving product family: " + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteProductFamily = async (productId) => {
    if (!productId) {
      console.error("Cannot delete product family: ID is undefined or null");
      alert(t('alert.errorDeleteProductFamily'));
      setDeleteConfirmProductFamily(null);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/product-families/${productId}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        // Refresh the tree view
        await fetchProductFamilies();
        setDeleteConfirmProductFamily(null);
      }
    } catch (error) {
      console.error("Error deleting product family:", error);
      alert("Error deleting product family");
    }
  };

  const handleAddChild = (parentProduct) => {
    setSelectedProductFamily(null);
    setSelectedParentId(parentProduct._id);
    setShowProductFamilyModal(true);
  };

  const handleCopyProduct = async (selectedChildIds, selectedParentId) => {
    if (!productToCopy) return;

    try {
      const res = await fetch(`${API_URL}/product-families/${productToCopy._id}/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          childIds: selectedChildIds,
          targetParentId: selectedParentId
        })
      });

      const data = await res.json();
      if (data.success) {
        // Refresh the tree to show the copied product
        fetchProductFamilies();
        setShowCopyModal(false);
        setProductToCopy(null);
      } else {
        alert(t('alert.errorCopyProduct') + ': ' + data.error);
      }
    } catch (error) {
      console.error('Error copying product:', error);
      alert(t('alert.errorCopyProduct'));
    }
  };

  const handleDuplicateAsColor = async ({ newColor, mlLink }) => {
    if (!productToColorDuplicate) return;
    const res = await fetch(`${API_URL}/product-families/${productToColorDuplicate._id}/duplicate-as-color`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newColor, mlLink })
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || 'Error al duplicar');
    }
    fetchProductFamilies();
    setProductToColorDuplicate(null);
  };

  const fetchConversationStatus = async (psid) => {
    try {
      const res = await API.get(`/api/conversation/${psid}/status`);
      setConversationStatuses(prev => ({
        ...prev,
        [psid]: res.data
      }));
    } catch (err) {
      console.error(`Error fetching status for ${psid}:`, err);
    }
  };

  const handleTakeover = async (psid) => {
    setHandoverLoading(prev => ({ ...prev, [psid]: true }));
    try {
      await API.post(`/api/conversation/${psid}/takeover`, {
        agentName: "Dashboard User",
        reason: "Manual takeover from dashboard"
      });

      await fetchConversationStatus(psid);
      toast.success(t('alert.takeoverSuccess'));
    } catch (err) {
      console.error("Error taking over:", err);
      toast.error(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setHandoverLoading(prev => ({ ...prev, [psid]: false }));
    }
  };

  const handleRelease = async (psid) => {
    setHandoverLoading(prev => ({ ...prev, [psid]: true }));
    try {
      await API.post(`/api/conversation/${psid}/release`);

      await fetchConversationStatus(psid);
      toast.success(t('alert.releaseSuccess'));
    } catch (err) {
      console.error("Error releasing:", err);
      toast.error(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setHandoverLoading(prev => ({ ...prev, [psid]: false }));
    }
  };

  useEffect(() => {
    fetchMessages();

    socket.on("new_message", (msg) => {
      setMessages((prev) => [msg, ...prev]);
      playPopSound();
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    if (location.pathname === "/products") {
      fetchProducts();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/campaigns" || location.pathname === "/" || location.pathname === "/analytics") {
      fetchCampaigns();
      fetchAdSets(); // Also fetch adsets for Ad modal dropdown
    }
    if (location.pathname === "/analytics") {
      setAnalyticsLoading(true);
      Promise.all([
        fetchClickStats(),
        fetchAdMetrics(),
        fetchTopProducts(),
        fetchTopRegion(),
        fetchLinksToday(),
        fetchPromotedProducts(),
        fetchHandoffsToday()
      ]).finally(() => setAnalyticsLoading(false));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/campaign-products") {
      fetchCampaignProducts();
      // Also fetch campaigns for the dropdown in the modal
      if (campaigns.length === 0) {
        fetchCampaigns();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/master-catalog") {
      fetchSubfamilies();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/usos-grupos") {
      fetchUsos();
      fetchGrupos();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/pos") {
      fetchPointsOfSale();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/familias") {
      fetchProductFamilies();
    }
  }, [location.pathname]);

  // Fetch notification unread count periodically
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_URL}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) setNotifUnreadCount(data.count);
      } catch (_) { /* silent */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000); // poll every 60s
    return () => clearInterval(interval);
  }, [user]);

  // Auth checks - must be AFTER all hooks
  // Show login page if not authenticated (except for login route)
  if (!authLoading && !user && location.pathname !== '/login') {
    return <Login />;
  }

  // If on login page but authenticated, redirect to landing page
  if (!authLoading && user && location.pathname === '/login') {
    navigate(getLandingPage());
    return null;
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">{t('app.loadingAuth')}</p>
        </div>
      </div>
    );
  }

  // Filter menu items based on user permissions
  // In simulation mode, use simulated role for visibility checks
  const effectiveRole = simulationMode?.role || user?.role;

  // Per-CHILD visibility. Some children are stricter than plain canAccess:
  //  - Bot builder tools (Flujos Legacy, intents) stay super_admin-only, even
  //    for admins (who otherwise have '*').
  //  - The ad↔flow assignment (ad-workflow) is open to super_admin, admin, and
  //    anyone explicitly granted it (e.g. the "Administrador de campaña" profile).
  const childVisible = (child) => {
    if (!child || child.isLabel) return true;
    // Spec Ops (killswitch / nuke) — super_admin only.
    if (child.id === 'killswitch' || child.id === 'nuke' || child.id === 'liberado') return effectiveRole === 'super_admin';
    if (child.id === 'intents' || child.id === 'ai-usage') return effectiveRole === 'super_admin';
    if (child.id === 'ad-workflow' || child.id === 'promos') {
      // Administrador de Campaña profile + above levels (super_user, admin, super_admin).
      return (
        effectiveRole === 'super_admin' ||
        effectiveRole === 'admin' ||
        effectiveRole === 'super_user' ||
        canAccess('ad-workflow')
      );
    }
    // Sandbox simulator: admins and above only (matches the backend, which allows
    // super_admin, admin, and super_user to list + run the sandbox).
    if (child.id === 'bot-sandbox') {
      return effectiveRole === 'super_admin' || effectiveRole === 'admin' || effectiveRole === 'super_user';
    }
    return canAccess(child.id);
  };

  const filteredMenuItems = menuItems.filter(item => {
    // Users section only visible to super_admin and admin
    if (item.id === 'users') {
      return canManageUsers();
    }
    // Seguimiento Ground Cover — super_user and above (super_user, admin, super_admin).
    if (item.id === 'gc-seguimiento') {
      return effectiveRole === 'super_admin' || effectiveRole === 'admin' || effectiveRole === 'super_user';
    }
    // Roles, Profiles, Playground, Spec Ops sections stay super_admin-only.
    if (item.id === 'roles' || item.id === 'profiles' || item.id === 'playground' || item.id === 'spec-ops') {
      return effectiveRole === 'super_admin';
    }
    // Bot section: visible if the user can see ANY of its children. With
    // childVisible, that means super_admin sees it (all children), while admins
    // and the campaign-admin profile see it for the ad-workflow item only.
    if (item.id === 'bot') {
      return (item.children || []).some(childVisible);
    }

    // For items with children (including CRM), check if user has access to ANY child
    if (item.children && item.children.length > 0) {
      return item.children.some(child => !child.isLabel && canAccess(child.id));
    }

    // Tickets and Notifications are visible to everyone
    if (item.id === 'tickets' || item.id === 'notifications') {
      return true;
    }

    // Overview is also visible for campaign-overview permission
    if (item.id === 'overview') {
      return canAccess('overview') || canAccess('campaign-overview');
    }

    // Filter other sections based on canAccess
    return canAccess(item.id);
  }).map(item => {
    // If item has children, filter them based on permissions (childVisible
    // applies the per-child rules above).
    if (item.children && item.children.length > 0) {
      return {
        ...item,
        children: item.children.filter(childVisible)
      };
    }
    return item;
  });

  // Helper function to get today's date range in Mexico City time
  const getTodayRange = () => {
    const now = new Date();
    const mexicoTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Mexico_City" }));

    const startOfDay = new Date(mexicoTime);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(mexicoTime);
    endOfDay.setHours(23, 59, 59, 999);

    return { start: startOfDay, end: endOfDay };
  };

  // Filter messages for today only
  const { start: todayStart, end: todayEnd } = getTodayRange();
  const todayMessages = messages.filter((m) => {
    const msgDate = new Date(m.timestamp);
    return msgDate >= todayStart && msgDate <= todayEnd;
  });

  // Metrics (using today's messages only)
  const totalMessages = todayMessages.length;
  const totalUsers = new Set(todayMessages.map((m) => m.psid)).size;
  const botMessages = todayMessages.filter((m) => m.senderType === "bot").length;
  const botResponseRate = totalMessages
    ? ((botMessages / totalMessages) * 100).toFixed(1)
    : 0;

  const lastMessagesByUser = {};
  todayMessages.forEach((m) => {
    if (!lastMessagesByUser[m.psid]) lastMessagesByUser[m.psid] = m;
  });
  const unanswered = Object.values(lastMessagesByUser).filter(
    (m) => m.senderType === "user"
  ).length;

  // Count active campaigns
  const activeCampaigns = campaigns.filter((c) => c.active === true).length;

  // Best ad comes from clickStats (aggregated from ClickLog)
  const bestAd = clickStats.bestAd;

  // Best conversion rate ad (minimum 3 clicks to be meaningful)
  const bestRateAd = clickStats.allAds
    .filter(ad => ad.clicks >= 3 && ad.conversions > 0)
    .sort((a, b) => parseFloat(b.conversionRate) - parseFloat(a.conversionRate))[0] || null;

  let filteredMessages = messages;

  // Apply sender type filter
  if (filter !== "all") {
    filteredMessages = filteredMessages.filter((msg) => msg.senderType === filter);
  }

  // Apply conversation filter (by PSID)
  if (conversationFilter) {
    filteredMessages = filteredMessages.filter((msg) => msg.psid === conversationFilter);
  }

  // Group messages by PSID to show only the latest message per conversation
  const latestMessagesByConversation = filteredMessages.reduce((acc, msg) => {
    if (!acc[msg.psid] || new Date(msg.timestamp) > new Date(acc[msg.psid].timestamp)) {
      acc[msg.psid] = msg;
    }
    return acc;
  }, {});

  // Get 10 most recent conversations
  filteredMessages = Object.values(latestMessagesByConversation)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 10);

  // Get user's full conversation history
  const getUserConversation = (psid) => {
    return messages
      .filter((msg) => msg.psid === psid)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Spec Ops: maintenance overlay for everyone below super_admin when halted */}
      <MaintenanceGate role={effectiveRole} />
      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1f2937',
            color: '#f3f4f6',
            border: '1px solid #374151',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#f3f4f6',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f3f4f6',
            },
          },
        }}
      />
      {/* Global Spec Ops banner — shown across the whole dashboard for everyone when
          a super_admin turns it on. */}
      {banner?.engaged && banner?.message && (
        <div className="fixed top-0 inset-x-0 z-[110] flex items-center justify-center gap-2 bg-amber-500 text-black text-sm font-semibold py-2 px-4 shadow-lg text-center">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" /></svg>
          <span>{banner.message}</span>
        </div>
      )}
      {/* Payment status banner — visible to admins across the whole system. Informative
          while due (by the 10th), red alert once overdue. Hidden once marked paid. */}
      {payment && (payment.status === 'due' || payment.status === 'overdue') && (effectiveRole === 'admin' || effectiveRole === 'super_admin') && (
        <div className={`fixed top-0 inset-x-0 z-[108] flex items-center justify-center gap-2 text-sm font-semibold py-2 px-4 shadow-lg text-center ${payment.status === 'overdue' ? 'bg-red-600 text-white' : 'bg-indigo-600 text-white'}`}>
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {payment.status === 'overdue'
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />}
          </svg>
          <span>{t(payment.status === 'overdue' ? 'payment.overdue' : 'payment.due', { price: payment.price, currency: payment.currency, day: payment.dueDay })}</span>
        </div>
      )}
      {/* OpenAI credit banner — SUPER_ADMIN ONLY, and the server only sends the data to
          super_admin, so this is gated twice on purpose: the balance is billing, not
          something an asesor can act on. Sits above every other banner (z-112) because
          at $0 the bot stops answering mid-conversation and nothing else warns — OpenAI
          alerts on spend against the monthly cap, not on a low prepaid balance. */}
      {openAiCredit && openAiCredit.low && effectiveRole === 'super_admin' && (
        <div className="fixed top-0 inset-x-0 z-[112] flex items-center justify-center gap-2 text-sm font-semibold py-2 px-4 shadow-lg text-center bg-amber-500 text-black">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" /></svg>
          <span>
            {openAiCredit.daysLeft != null
              ? t('credit.lowBanner', {
                  amount: `$${Number(openAiCredit.remaining).toFixed(2)}`,
                  days: openAiCredit.daysLeft.toFixed(1),
                })
              : t('credit.lowBannerNoDays', { amount: `$${Number(openAiCredit.remaining).toFixed(2)}` })}
          </span>
          <a href="/bot/costos-ia" className="underline font-bold">{t('credit.review')}</a>
        </div>
      )}
      {/* New version banner — sits ABOVE the payment banner (z-109 > 108) so the
          "reload" action is never covered; still below the Spec Ops banner (z-110). */}
      {updateAvailable && (
        <div className="fixed top-0 inset-x-0 z-[109] flex items-center justify-center gap-3 bg-blue-600 text-white text-sm py-2 px-4 shadow-lg">
          <span>{t('app.updateAvailable')}</span>
          <button
            onClick={() => window.location.reload()}
            className="bg-white text-blue-700 font-semibold px-3 py-0.5 rounded hover:bg-blue-50 transition-colors"
          >
            {t('app.updateButton')}
          </button>
        </div>
      )}
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-gray-900/95 backdrop-blur-lg border-r border-gray-700/50 z-50 transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <Link to="/" className="block p-6 border-b border-gray-700/50 hover:bg-gray-800/50 transition-colors">
            <div className="flex items-center space-x-3">
              <img src="/logo192.png" alt="Hanlob" className="w-10 h-10 rounded-lg" />
              <div>
                <h2 className="text-lg font-bold text-white">Hanlob Bot</h2>
                <p className="text-xs text-gray-400">Dashboard</p>
              </div>
            </div>
          </Link>

          {/* Menu Items */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredMenuItems.map((item) => {
              // Expandable section
              if (item.isExpandable && item.children) {
                const isExpanded = expandedSections[item.id];
                return (
                  <div key={item.id} className="space-y-1">
                    {/* Section Header */}
                    <button
                      onClick={() => setExpandedSections(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800/50 hover:text-white transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {item.icon}
                        </svg>
                        <span className="font-medium">{t(item.labelKey)}</span>
                      </div>
                      <svg
                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Submenu Items */}
                    {isExpanded && (
                      <div className="ml-4 space-y-1 border-l-2 border-gray-700/50 pl-2">
                        {item.children.map((child) => (
                          child.isLabel ? (
                            <p key={child.id} className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold px-4 pt-3 pb-1">{t(child.labelKey)}</p>
                          ) : (
                          <NavLink
                            key={child.id}
                            to={child.path}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) => `w-full flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                              isActive
                                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                                : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                            }`}
                            end
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {child.icon}
                            </svg>
                            <span className="font-medium">{t(child.labelKey)}</span>
                          </NavLink>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              // Regular menu item
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                  }`}
                  end={item.path === "/"}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {item.icon}
                  </svg>
                  <span className="font-medium">{t(item.labelKey)}</span>
                  {item.id === 'notifications' && notifUnreadCount > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white min-w-[20px]">
                      {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-gray-700/50 space-y-3">
            {/* User Info */}
            <div className="bg-gray-800/50 rounded-lg p-3">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                  <span className="text-primary-400 font-semibold">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.fullName}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.roleLabel}</p>
                  {user?.profileLabel && (
                    <p className="text-xs text-gray-500 truncate">{user?.profileLabel}</p>
                  )}
                </div>
              </div>
              <button
                onClick={logout}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>{t('app.logout')}</span>
              </button>
            </div>

            {/* Live Indicator */}
            <div className="flex items-center space-x-2 bg-primary-500/10 px-3 py-2 rounded-lg">
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-primary-400">{t('app.live')}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-gray-900/50 backdrop-blur-lg border-b border-gray-700/50 sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              {/* Left: Hamburger + Title */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 rounded-lg text-gray-400 hover:bg-gray-800/50 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    {(() => { const mi = menuItems.find(item => item.path === location.pathname); return mi ? t(mi.labelKey) : t('menu.overview'); })()}
                  </h1>
                  <p className="text-sm text-gray-400">{t('app.subtitle')}</p>
                </div>
              </div>

              {/* Right: Top Menu */}
              <div className="flex items-center space-x-2">
                {/* Simulation Mode Selector (super_admin only) */}
                <SimulationModeSelector />

                {/* Language Toggle */}
                <button
                  onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
                  className="px-2 py-1 rounded-lg text-xs font-bold text-gray-400 hover:bg-gray-800/50 hover:text-white transition-colors border border-gray-700/50"
                  title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
                >
                  {language === 'es' ? 'EN' : 'ES'}
                </button>

                {/* Notifications */}
                <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-800/50 hover:text-white transition-colors relative">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"></span>
                </button>

                {/* Help */}
                <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-800/50 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>

                {/* User Profile */}
                <button className="flex items-center space-x-2 p-2 rounded-lg text-gray-400 hover:bg-gray-800/50 hover:text-white transition-colors">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">A</span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-white">{t('app.admin')}</span>
                </button>
              </div>
            </div>
          </div>
        </header>

      {/* Simulation Mode Banner */}
      {simulationMode && (
        <div className="bg-amber-500/10 border-b border-amber-500/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span className="text-sm text-amber-200">
                  <strong>{t('simulation.title')}</strong> {t('simulation.viewingAs')} <span className="font-semibold text-amber-100">{simulationMode.label}</span>
                </span>
                <span className="text-xs text-amber-400/70">
                  ({simulationMode.permissions?.includes('*') ? t('simulation.allPermissions') : t('simulation.permissionsCount', { count: simulationMode.permissions?.length || 0 })})
                </span>
              </div>
              <button
                onClick={stopSimulation}
                className="text-sm text-amber-400 hover:text-amber-300 flex items-center space-x-1 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>{t('simulation.exit')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Health Alerts */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <ApiHealthAlerts />
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          {/* Login Route (Public) */}
          <Route path="/login" element={<Login />} />

          {/* Password Recovery Routes (Public) */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Overview Route */}
          <Route path="/" element={canAccess('overview') ? <Home /> : canAccess('campaign-overview') ? <CampaignHome /> : <Navigate to={getLandingPage()} replace />} />

          {/* Sales Overview Route */}
          <Route path="/sales-overview" element={<SalesOverviewView />} />

          {/* Conversaciones Route */}
          <Route path="/conversations" element={<Messages />} />

          {/* Analytics Route */}
          <Route path="/analytics" element={analyticsLoading ? (
          <div className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
              <p className="mt-4 text-gray-400">{t('home.loadingDashboard')}</p>
            </div>
          </div>
          ) : (
          <div className="space-y-8">
            {/* Category: Mensajes del día */}
            {canAccess('stats_messages') && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('analytics.dailyMessages')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Total Messages */}
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 backdrop-blur-lg border border-blue-500/20 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('overview.totalMessages')}</p>
                        <h3 className="text-3xl font-bold text-white">{totalMessages}</h3>
                      </div>
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Unique Users */}
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 backdrop-blur-lg border border-purple-500/20 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('overview.uniqueUsers')}</p>
                        <h3 className="text-3xl font-bold text-white">{totalUsers}</h3>
                      </div>
                      <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Response Rate */}
                  <div className="bg-gradient-to-br from-primary-500/10 to-primary-600/5 backdrop-blur-lg border border-primary-500/20 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('overview.responseRate')}</p>
                        <h3 className="text-3xl font-bold text-white">{botResponseRate}%</h3>
                      </div>
                      <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Unanswered */}
                  <div onClick={() => navigate('/conversations')} className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 backdrop-blur-lg border border-amber-500/20 rounded-xl p-6 cursor-pointer hover:border-amber-500/40 hover:scale-[1.02] transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('overview.unanswered')}</p>
                        <h3 className="text-3xl font-bold text-white">{unanswered}</h3>
                      </div>
                      <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Links servidos hoy */}
                  <div onClick={() => navigate('/click-logs')} className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 backdrop-blur-lg border border-indigo-500/20 rounded-xl p-6 cursor-pointer hover:border-indigo-500/40 hover:scale-[1.02] transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">Links servidos</p>
                        <h3 className="text-3xl font-bold text-white">{linksToday.served}</h3>
                      </div>
                      <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m8.485-2.828a4 4 0 00-5.656 0l-1.5 1.5" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Links clicados hoy */}
                  <div onClick={() => navigate('/click-logs')} className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 backdrop-blur-lg border border-cyan-500/20 rounded-xl p-6 cursor-pointer hover:border-cyan-500/40 hover:scale-[1.02] transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">Links clicados</p>
                        <h3 className="text-3xl font-bold text-white">{linksToday.clicked}</h3>
                      </div>
                      <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Handoffs hoy */}
                  <div onClick={() => navigate('/conversations')} className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 backdrop-blur-lg border border-orange-500/20 rounded-xl p-6 cursor-pointer hover:border-orange-500/40 hover:scale-[1.02] transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">Handoffs</p>
                        <h3 className="text-3xl font-bold text-white">{handoffsToday}</h3>
                      </div>
                      <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Top Region */}
                  {topRegion && (
                    <div onClick={() => navigate('/geo')} className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 backdrop-blur-lg border border-purple-500/20 rounded-xl p-6 cursor-pointer hover:border-purple-500/40 hover:scale-[1.02] transition-all">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">{t('overview.topRegion')}</p>
                          <h3 className="text-2xl font-bold text-white">{/^(estado de m[eé]xico|m[eé]xico)$/i.test((topRegion.state || '').trim()) ? 'Edomex' : topRegion.state}</h3>
                          <p className="text-sm text-purple-400">{topRegion.conversations} {t('overview.conversations')}</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                          <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Divider */}
            {canAccess('stats_messages') && canAccess('stats_campaigns') && (
              <hr className="border-gray-700/50" />
            )}

            {/* Category: Campañas activas */}
            {canAccess('stats_campaigns') && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('analytics.activeCampaigns')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Active Campaigns */}
                  <Link to="/campaigns" className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 backdrop-blur-lg border border-emerald-500/20 rounded-xl p-6 hover:border-emerald-500/40 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('overview.activeCampaigns')}</p>
                        <h3 className="text-3xl font-bold text-white">{activeCampaigns}</h3>
                      </div>
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                        </svg>
                      </div>
                    </div>
                  </Link>

                  {/* Best Ad */}
                  <Link to="/campaigns" className="bg-gradient-to-br from-rose-500/10 to-rose-600/5 backdrop-blur-lg border border-rose-500/20 rounded-xl p-6 hover:border-rose-500/40 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('analytics.mostClickedAd')}</p>
                        <h3 className="text-lg font-bold text-white truncate max-w-[150px]">{bestAd?.name || '-'}</h3>
                        <p className="text-sm text-rose-400">{bestAd?.clicks || 0} {t('analytics.clicks')}</p>
                      </div>
                      <div className="w-12 h-12 bg-rose-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                    </div>
                  </Link>

                  {/* Click-Through Rate */}
                  <Link to="/click-logs" className="bg-gradient-to-br from-teal-500/10 to-teal-600/5 backdrop-blur-lg border border-teal-500/20 rounded-xl p-6 hover:border-teal-500/40 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('analytics.ctr')}</p>
                        <h3 className="text-3xl font-bold text-white">{adMetrics.ctr}%</h3>
                        <p className="text-sm text-teal-400">{adMetrics.totalClicks.toLocaleString()} / {adMetrics.totalImpressions.toLocaleString()}</p>
                      </div>
                      <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                        </svg>
                      </div>
                    </div>
                  </Link>

                  {/* Productos promocionados — cuántas líneas de producto se anuncian */}
                  <Link to="/ad-performance" className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 backdrop-blur-lg border border-indigo-500/20 rounded-xl p-6 hover:border-indigo-500/40 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">Productos promocionados</p>
                        <h3 className="text-3xl font-bold text-white">{promotedProducts}</h3>
                        <p className="text-sm text-indigo-400">líneas de producto</p>
                      </div>
                      <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                    </div>
                  </Link>

                </div>
              </div>
            )}

            {/* Divider */}
            {canAccess('stats_campaigns') && (
              <hr className="border-gray-700/50" />
            )}

            {/* Category: Ventas */}
            {canAccess('stats_campaigns') && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('analytics.sales')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Best Conversion Rate Ad */}
                  <Link to="/conversions" className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 backdrop-blur-lg border border-yellow-500/20 rounded-xl p-6 hover:border-yellow-500/40 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('analytics.bestConversionRate')}</p>
                        <h3 className="text-lg font-bold text-white truncate max-w-[150px]">{bestRateAd?.name || '-'}</h3>
                        <p className="text-sm text-yellow-400">{bestRateAd?.conversionRate || 0}% ({bestRateAd?.conversions || 0} {t('analytics.salesCount')})</p>
                      </div>
                      <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </Link>

                  {/* Best Selling Product */}
                  <Link to="/click-logs" className="bg-gradient-to-br from-green-500/10 to-green-600/5 backdrop-blur-lg border border-green-500/20 rounded-xl p-6 hover:border-green-500/40 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-1">{t('analytics.bestSellingProduct')}</p>
                        <h3 className="text-lg font-bold text-white truncate max-w-[150px]">{topProduct?._id || '-'}</h3>
                        <p className="text-sm text-green-400">{topProduct?.conversions || 0} {t('analytics.salesCount')}</p>
                      </div>
                      <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>
          )} />

          {/* Products Route */}
          <Route path="/products" element={
            <ProductsView
              products={products}
              loading={productsLoading}
              onAdd={() => {
                setEditingProduct(null);
                setShowProductModal(true);
              }}
              onEdit={(product) => {
                setEditingProduct(product);
                setShowProductModal(true);
              }}
              onDelete={(product) => {
                setDeleteConfirmProduct(product);
              }}
            />
          } />

          {/* Campaigns Route */}
          <Route path="/campaigns" element={
            <CampaignTreeView
              campaigns={campaigns}
              loading={campaignsLoading}
              editingItem={editingCampaign}
              onRefresh={fetchCampaigns}
              onAdd={() => {
                setEditingCampaign(null);
                setShowCampaignModal(true);
              }}
              onEdit={(item) => {
                console.log('🔍 Edit clicked - Item:', item);
                console.log('🔍 Item type:', item.type);
                console.log('🔍 Item name:', item.name);
                console.log('🔍 Has fbAdId?', !!item.fbAdId);
                console.log('🔍 Has adSetId?', !!item.adSetId);

                const itemType = item.type || 'campaign';
                console.log('🔍 Determined type:', itemType);

                if (itemType === 'campaign') {
                  console.log('✅ Opening Campaign modal');
                  setEditingCampaign(item);
                  setShowCampaignModal(true);
                } else if (itemType === 'adset') {
                  console.log('✅ Opening AdSet modal');
                  setEditingAdSet(item);
                  setShowAdSetModal(true);
                } else if (itemType === 'ad') {
                  console.log('✅ Opening Ad modal');
                  setEditingAd(item);
                  setShowAdModal(true);
                }
              }}
              onDelete={(item) => {
                setDeleteConfirmItem(item);
              }}
              onAddChild={(parent) => {
                const parentType = parent.type || 'campaign';
                if (parentType === 'campaign') {
                  // Add adset to campaign
                  setParentCampaignId(parent._id);
                  setEditingAdSet(null);
                  setShowAdSetModal(true);
                } else if (parentType === 'adset') {
                  // Add ad to adset
                  setParentAdSetId(parent._id);
                  setEditingAd(null);
                  setShowAdModal(true);
                }
              }}
              onDetails={(item) => {
                setDetailsItem(item);
              }}
              onUpdateCampaign={(id, updates) => {
                setCampaigns(prev => prev.map(c => {
                  if (c._id !== id) return c;
                  const updated = { ...c, ...updates };
                  // Cascade active → children status
                  if (updates.active !== undefined && c.children) {
                    const newStatus = updates.active ? 'ACTIVE' : 'PAUSED';
                    updated.children = c.children.map(adSet => ({
                      ...adSet,
                      status: newStatus,
                      children: adSet.children ? adSet.children.map(ad => ({
                        ...ad,
                        status: newStatus
                      })) : []
                    }));
                  }
                  return updated;
                }));
              }}
            />
          } />

          {/* AdSets Route */}
          <Route path="/adsets" element={<AdSetsView />} />

          {/* Ads Route */}
          <Route path="/ads" element={<AdsView />} />

          {/* Tracked Links Route */}
          <Route path="/tracked-links" element={<TrackedLinksView />} />

          {/* Flujos Route */}
          <Route path="/workflows" element={<WorkflowsView />} />
          <Route path="/playground/simulador" element={<ConvoSimulatorView />} />
          <Route path="/bot/simulador" element={<ConvoSimulatorView sandboxOnly />} />
          <Route path="/promos" element={<PromosView />} />
          <Route path="/anuncio-flujo" element={(effectiveRole === 'super_admin' || effectiveRole === 'admin' || effectiveRole === 'super_user' || canAccess('ad-workflow')) ? <AdWorkflowAssignView /> : <Navigate to={getLandingPage()} replace />} />
          {/* Legacy path (was under playground) → new location */}
          <Route path="/playground/anuncio-flujo" element={<Navigate to="/anuncio-flujo" replace />} />
          <Route path="/inteligencia-artificial" element={<CampaignIntelligenceView />} />
          <Route path="/ml-import" element={<MLOrderImportView />} />

          {/* Master Catalog Route */}
          <Route path="/master-catalog" element={
            <MasterCatalogView
              subfamilies={subfamilies}
              loading={subfamiliesLoading}
              onAdd={() => {
                setEditingSubfamily(null);
                setShowMasterCatalogModal(true);
              }}
              onEdit={(subfamily) => {
                setEditingSubfamily(subfamily);
                setShowMasterCatalogModal(true);
              }}
              onDelete={(subfamily) => {
                setDeleteConfirmSubfamily(subfamily);
              }}
            />
          } />

          {/* Usos y Grupos Route */}
          <Route path="/usos-grupos" element={
            <UsosYGruposView
              usos={usos}
              usosLoading={loading}
              onAddUso={() => {
                setSelectedUso(null);
                setShowUsosModal(true);
              }}
              onEditUso={(uso) => {
                setSelectedUso(uso);
                setShowUsosModal(true);
              }}
              onDeleteUso={(uso) => handleDeleteUso(uso)}
              grupos={grupos}
              gruposLoading={loading}
              onAddGrupo={() => {
                setSelectedGrupo(null);
                setShowGruposModal(true);
              }}
              onEditGrupo={(grupo) => {
                setSelectedGrupo(grupo);
                setShowGruposModal(true);
              }}
              onDeleteGrupo={(grupo) => handleDeleteGrupo(grupo)}
            />
          } />

          {/* Puntos de Venta Route */}
          <Route path="/pos" element={
            <PuntosDeVentaView
              pointsOfSale={pointsOfSale}
              loading={pointsOfSaleLoading}
              onAdd={() => {
                setSelectedPuntoDeVenta(null);
                setShowPuntoDeVentaModal(true);
              }}
              onEdit={(pos) => {
                setSelectedPuntoDeVenta(pos);
                setShowPuntoDeVentaModal(true);
              }}
              onDelete={(pos) => handleDeletePuntoDeVenta(pos)}
            />
          } />

          {/* Familias Route */}
          <Route path="/familias" element={
            <ProductFamilyTreeView
              products={productFamilyTree}
              loading={productFamiliesLoading}
              editingProduct={selectedProductFamily}
              onAdd={() => {
                setSelectedProductFamily(null);
                setSelectedParentId(null);
                setShowProductFamilyModal(true);
              }}
              onEdit={(product) => {
                setSelectedProductFamily(product);
                setSelectedParentId(null);
                setShowProductFamilyModal(true);
              }}
              onDelete={(product) => {
                setDeleteConfirmProductFamily(product);
              }}
              onAddChild={handleAddChild}
              onCopy={(product) => {
                setProductToCopy(product);
                setShowCopyModal(true);
              }}
              onDuplicateColor={(product) => {
                setProductToColorDuplicate(product);
              }}
              onImport={(product) => {
                setImportTargetFamily(product);
                setShowImportModal(true);
              }}
              onDetails={(product, parentChain = []) => {
                setProductToShowDetails(product);
                setProductDetailsParentChain(parentChain);
                setShowDetailsModal(true);
              }}
            />
          } />

          {/* Inventario Route */}
          <Route path="/inventario" element={<InventarioView />} />
          <Route path="/consumo" element={canAccess('consumo') ? <ConsumoView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/playground/mapa" element={effectiveRole === 'super_admin' ? <MapaView /> : <Navigate to={getLandingPage()} replace />} />

          {/* Users, Roles, and Profiles Routes */}
          <Route path="/users" element={<UsersView />} />
          <Route path="/roles" element={<RolesView />} />
          <Route path="/profiles" element={<ProfilesView />} />
          <Route path="/click-logs" element={<ClickLogsView />} />
          <Route path="/ad-performance" element={<AdPerformanceView />} />
          <Route path="/segmentacion" element={<CustomerSegmentationView />} />
          <Route path="/ad-performance/:fbAdId" element={<AdDetailView />} />
          <Route path="/geo" element={<GeoDetailView />} />
          <Route path="/conversions" element={<ConversionsView />} />
          <Route path="/manual-sales" element={<ManualSalesView />} />

          {/* CRM Routes */}
          <Route path="/crm" element={<CRMView />} />
          <Route path="/crm/sales" element={<CRMSalesView />} />
          <Route path="/crm/:psid" element={<CustomerDetailView />} />

          {/* Mercado Libre Routes */}
          <Route path="/ml-orders" element={<OrdersView />} />
          <Route path="/ml-importer" element={<MLImporterView />} />
          <Route path="/playground/mercado-libre" element={<MLPlaygroundView />} />
          <Route path="/forecast" element={<SalesForecastView mode="engagement" />} />
          <Route path="/playground/conversion" element={<ConversionProbabilityView />} />
          <Route path="/playground/rendimiento-mensajes" element={<MessagePerformanceView />} />
          <Route path="/playground/seguimiento-ground-cover" element={<GcFollowUpView />} />


          {/* Tickets Route */}
          <Route path="/tickets" element={<TicketsView />} />

          {/* Notifications Route */}
          <Route path="/notifications" element={<NotificationsView />} />

          {/* Cross-Selling Route */}
          <Route path="/cross-sell" element={<CrossSellView />} />
          <Route path="/company-info" element={<CompanyInfoView />} />

          <Route path="/settings" element={<Settings />} />
          <Route path="/reported-convos" element={effectiveRole === 'super_admin' ? <ReportedConversationsView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/bot/costos-ia" element={effectiveRole === 'super_admin' ? <AiUsageView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/spec-ops/killswitch" element={effectiveRole === 'super_admin' ? <KillswitchView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/spec-ops/nuke" element={effectiveRole === 'super_admin' ? <NukeEmView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/spec-ops/liberado" element={effectiveRole === 'super_admin' ? <LiberadoView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/spec-ops/banner" element={effectiveRole === 'super_admin' ? <BannerView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/spec-ops/pago" element={effectiveRole === 'super_admin' ? <PaymentView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/spec-ops/fb-comment-reply" element={effectiveRole === 'super_admin' ? <FbCommentReplyView /> : <Navigate to={getLandingPage()} replace />} />
          <Route path="/help" element={<HelpView />} />
        </Routes>

        {/* Product Modal */}
        {showProductModal && (
          <ProductModal
            product={editingProduct}
            onSave={handleSaveProduct}
            onClose={() => {
              setShowProductModal(false);
              setEditingProduct(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmProduct && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800/95 backdrop-blur-lg border border-gray-700/50 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t('delete.deleteProduct')}</h3>
                  <p className="text-sm text-gray-400">{t('delete.cannotUndo')}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                {t('delete.confirmDelete')} <span className="font-semibold text-white">{deleteConfirmProduct.name}</span>?
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDeleteConfirmProduct(null)}
                  className="flex-1 px-4 py-2 bg-gray-700/50 text-white rounded-lg hover:bg-gray-600/50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleDeleteProduct(deleteConfirmProduct._id)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Campaign Modal */}
        {showCampaignModal && (
          <CampaignModal
            campaign={editingCampaign}
            onSave={handleSaveCampaign}
            onClose={() => {
              setShowCampaignModal(false);
              setEditingCampaign(null);
            }}
          />
        )}

        {/* Campaign Delete Confirmation Modal (legacy - kept for other campaign delete flows) */}
        {deleteConfirmCampaign && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800/95 backdrop-blur-lg border border-gray-700/50 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t('delete.deleteCampaign')}</h3>
                  <p className="text-sm text-gray-400">{t('delete.cannotUndo')}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                {t('delete.confirmDelete')} <span className="font-semibold text-white">{deleteConfirmCampaign.name}</span>?
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDeleteConfirmCampaign(null)}
                  className="flex-1 px-4 py-2 bg-gray-700/50 text-white rounded-lg hover:bg-gray-600/50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleDeleteCampaign(deleteConfirmCampaign._id)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AdSet Modal */}
        {showAdSetModal && (
          <AdSetModal
            adSet={editingAdSet}
            campaigns={campaigns}
            parentCampaignId={parentCampaignId}
            onSave={handleSaveAdSet}
            onClose={() => {
              setShowAdSetModal(false);
              setEditingAdSet(null);
              setParentCampaignId(null);
            }}
          />
        )}

        {/* Ad Modal */}
        {showAdModal && (
          <AdModal
            ad={editingAd}
            adSets={adSets}
            parentAdSetId={parentAdSetId}
            onSave={handleSaveAd}
            onClose={() => {
              setShowAdModal(false);
              setEditingAd(null);
              setParentAdSetId(null);
            }}
          />
        )}

        {/* Campaign Item Details Modal */}
        {detailsItem && (
          <CampaignItemDetailsModal
            item={detailsItem}
            onClose={() => setDetailsItem(null)}
          />
        )}

        {/* Unified Delete Confirmation Modal for Campaigns/AdSets/Ads */}
        {deleteConfirmItem && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800/95 backdrop-blur-lg border border-gray-700/50 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t('delete.title')}</h3>
                  <p className="text-sm text-gray-400">{t('delete.cannotUndo')}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                {t('delete.confirmDelete')} "{deleteConfirmItem.name || t('delete.thisElement')}"?
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDeleteConfirmItem(null)}
                  className="flex-1 px-4 py-2 bg-gray-700/50 text-white rounded-lg hover:bg-gray-600/50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleDeleteItem(deleteConfirmItem)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Campaign Product Modal */}
        {showCampaignProductModal && (
          <CampaignProductModal
            campaignProduct={editingCampaignProduct}
            campaigns={campaigns}
            onSave={handleSaveCampaignProduct}
            onClose={() => {
              setShowCampaignProductModal(false);
              setEditingCampaignProduct(null);
            }}
          />
        )}

        {/* Campaign Product Delete Confirmation Modal */}
        {deleteConfirmCampaignProduct && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800/95 backdrop-blur-lg border border-gray-700/50 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t('delete.deleteCampaignProduct')}</h3>
                  <p className="text-sm text-gray-400">{t('delete.cannotUndo')}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                {t('delete.confirmDelete')} <span className="font-semibold text-white">{deleteConfirmCampaignProduct.name}</span>?
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDeleteConfirmCampaignProduct(null)}
                  className="flex-1 px-4 py-2 bg-gray-700/50 text-white rounded-lg hover:bg-gray-600/50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleDeleteCampaignProduct(deleteConfirmCampaignProduct._id)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* AdSet Modal */}
        {showAdSetModal && (
          <AdSetModal
            adSet={editingAdSet}
            campaigns={campaigns}
            parentCampaignId={parentCampaignId}
            onSave={handleSaveAdSet}
            onClose={() => {
              setShowAdSetModal(false);
              setEditingAdSet(null);
              setParentCampaignId(null);
            }}
          />
        )}

        {/* Ad Modal */}
        {showAdModal && (
          <AdModal
            ad={editingAd}
            adSets={adSets}
            parentAdSetId={parentAdSetId}
            onSave={handleSaveAd}
            onClose={() => {
              setShowAdModal(false);
              setEditingAd(null);
              setParentAdSetId(null);
            }}
          />
        )}

        {/* Campaign Item Details Modal */}
        {detailsItem && (
          <CampaignItemDetailsModal
            item={detailsItem}
            onClose={() => setDetailsItem(null)}
          />
        )}

        {/* Campaign Tree Delete Confirmation Modal */}
        {deleteConfirmItem && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800/95 backdrop-blur-lg border border-gray-700/50 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {t('common.delete')} {deleteConfirmItem.type === 'campaign' ? t('delete.campaignLabel') : deleteConfirmItem.type === 'adset' ? t('delete.adSetLabel') : t('delete.adLabel')}
                  </h3>
                  <p className="text-sm text-gray-400">{t('delete.cannotUndo')}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                {t('delete.confirmDelete')} <span className="font-semibold text-white">{deleteConfirmItem.name}</span>?
                {deleteConfirmItem.type === 'campaign' && ` ${t('delete.cascadeCampaign')}`}
                {deleteConfirmItem.type === 'adset' && ` ${t('delete.cascadeAdSet')}`}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDeleteConfirmItem(null)}
                  className="flex-1 px-4 py-2 bg-gray-700/50 text-white rounded-lg hover:bg-gray-600/50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleDeleteItem(deleteConfirmItem)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Master Catalog Modal */}
        {showMasterCatalogModal && (
          <MasterCatalogModal
            subfamily={editingSubfamily}
            onSave={handleSaveSubfamily}
            onClose={() => {
              setShowMasterCatalogModal(false);
              setEditingSubfamily(null);
            }}
          />
        )}

        {/* Usos Modal */}
        {showUsosModal && (
          <UsosModal
            uso={selectedUso}
            onSave={handleSaveUso}
            onClose={() => {
              setShowUsosModal(false);
              setSelectedUso(null);
            }}
          />
        )}

        {/* Grupos Modal */}
        {showGruposModal && (
          <GruposModal
            grupo={selectedGrupo}
            onSave={handleSaveGrupo}
            onClose={() => {
              setShowGruposModal(false);
              setSelectedGrupo(null);
            }}
          />
        )}

        {/* Punto de Venta Modal */}
        {showPuntoDeVentaModal && (
          <PuntoDeVentaModal
            puntoDeVenta={selectedPuntoDeVenta}
            onSave={handleSavePuntoDeVenta}
            onClose={() => {
              setShowPuntoDeVentaModal(false);
              setSelectedPuntoDeVenta(null);
            }}
          />
        )}

        {/* Product Family Modal */}
        {showProductFamilyModal && (
          <ProductFamilyModal
            product={selectedProductFamily}
            allProducts={productFamilyTree}
            presetParentId={selectedParentId}
            onSave={handleSaveProductFamily}
            onClose={(changed) => {
              setShowProductFamilyModal(false);
              setSelectedProductFamily(null);
              setSelectedParentId(null);
              // Only reload if the modal made a server-side change outside the save
              // flow (e.g. catalog upload/delete). A plain cancel must NOT reload.
              if (changed) fetchProductFamilies();
            }}
          />
        )}

        {/* Copy Product Modal */}
        {showCopyModal && productToCopy && (
          <CopyProductModal
            product={productToCopy}
            onConfirm={handleCopyProduct}
            onCancel={() => {
              setShowCopyModal(false);
              setProductToCopy(null);
            }}
          />
        )}

        {/* Duplicate as Color Modal */}
        {productToColorDuplicate && (
          <DuplicateAsColorModal
            product={productToColorDuplicate}
            onConfirm={handleDuplicateAsColor}
            onCancel={() => setProductToColorDuplicate(null)}
          />
        )}

        {/* Product Details Modal */}
        {showDetailsModal && productToShowDetails && (
          <ProductDetailsModal
            product={productToShowDetails}
            parentChain={productDetailsParentChain}
            onClose={() => {
              setShowDetailsModal(false);
              setProductToShowDetails(null);
              setProductDetailsParentChain([]);
            }}
          />
        )}

        {/* Import Products Modal */}
        {showImportModal && importTargetFamily && (
          <ImportProductsModal
            targetFamily={importTargetFamily}
            onClose={() => {
              setShowImportModal(false);
              setImportTargetFamily(null);
            }}
            onImport={() => {
              setShowImportModal(false);
              setImportTargetFamily(null);
              fetchProductFamilies();
            }}
          />
        )}

        {/* Product Family Delete Confirmation Modal */}
        {deleteConfirmProductFamily && deleteConfirmProductFamily._id && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800/95 backdrop-blur-lg border border-gray-700/50 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t('delete.deleteProductFamily')}</h3>
                  <p className="text-sm text-gray-400">{t('delete.cannotUndo')}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                {t('delete.confirmDelete')} <span className="font-semibold text-white">{deleteConfirmProductFamily.name}</span>? {t('delete.cascadeChildren')}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDeleteConfirmProductFamily(null)}
                  className="flex-1 px-4 py-2 bg-gray-700/50 text-white rounded-lg hover:bg-gray-600/50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleDeleteProductFamily(deleteConfirmProductFamily._id)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Master Catalog Delete Confirmation Modal */}
        {deleteConfirmSubfamily && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800/95 backdrop-blur-lg border border-gray-700/50 rounded-xl max-w-md w-full p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t('delete.deleteCatalogEntry')}</h3>
                  <p className="text-sm text-gray-400">{t('delete.cannotUndo')}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6">
                {t('delete.confirmDelete')} <span className="font-semibold text-white">{deleteConfirmSubfamily.name}</span>?
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setDeleteConfirmSubfamily(null)}
                  className="flex-1 px-4 py-2 bg-gray-700/50 text-white rounded-lg hover:bg-gray-600/50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleDeleteSubfamily(deleteConfirmSubfamily._id)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Conversation Detail Modal - Global */}
        {selectedConversation && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setSelectedConversation(null)}
          >
            <div
              className="bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center p-4 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white">
                  {t('overview.conversationDetail')}
                </h2>
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  &times;
                </button>
              </div>

              {/* Modal Body - Scrollable Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {getUserConversation(selectedConversation).map((msg, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg ${
                      msg.senderType === "bot"
                        ? "bg-blue-900 text-blue-100"
                        : "bg-gray-800 text-gray-100"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs font-semibold uppercase">
                        {msg.senderType === "bot" ? t('overview.bot') : t('overview.userLabel')}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(msg.timestamp).toLocaleString(locale)}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-700 space-y-3">
                {/* Status Indicator */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Estado actual:</span>
                  {conversationStatuses[selectedConversation]?.humanActive ? (
                    <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-medium">
                      👨‍💼 Humano Activo
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium">
                      🤖 Bot Activo
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {conversationStatuses[selectedConversation]?.humanActive ? (
                    <button
                      onClick={() => handleRelease(selectedConversation)}
                      disabled={handoverLoading[selectedConversation]}
                      className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {handoverLoading[selectedConversation] ? "..." : "🤖 Liberar al Bot"}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleTakeover(selectedConversation)}
                      disabled={handoverLoading[selectedConversation]}
                      className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {handoverLoading[selectedConversation] ? "..." : "👨‍💼 Tomar Control"}
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setSelectedConversation(null);
                      navigate("/conversations");
                    }}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Ver en Tabla
                  </button>

                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
      </div>
    </div>
  );
}

export default App;
